<template>
  <section id="order" class="order-steps-container">
    <arch-small
      class="arch-small"
      :steps="steps"
      :activeStep="activeStep"
      @go-to-step="goToStep"
    ></arch-small>

    <section>
      <router-view></router-view>
    </section>
  </section>
</template>

<script>
import ArchSmall from '../../components/ArchSmall.vue'
import isEmpty from 'lodash-es/isEmpty'
import { EventBus } from '@/eventbus/EventBus'
import OrderMixin from '@/mixins/OrderMixin'
import SendingOrder from '@/dialogs/SendingOrder.vue'
import OrderConfirm from '@/dialogs/OrderConfirm.vue'
import SapOrderConfirm from '@/dialogs/SapOrderConfirm.vue'
import OrderRedirectConfirm from '@/dialogs/OrderRedirectConfirm.vue'
import ErrorMessage from '@/dialogs/ErrorMessage.vue'

import cloneDeep from 'lodash-es/cloneDeep'

import { mapStores } from 'pinia'
import { useCheckOutStore } from '@/features/checkOut/CheckOut.store'
import { saveOrder, userSetting } from '@/api/api'
import { useFileListStore } from '@/stores/FileList.store'

import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'

export default {
  components: { ArchSmall },
  mixins: [OrderMixin],
  props: {
    secure: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inProgress: false
    }
  },
  methods: {
    goToStep(index) {
      //['Producten', 'Bezorgen', 'Factureren', 'Controleren']
      let step = this.steps[index]
      // this.activeStep = index
      switch (step) {
        case 'Producten':
          this.$router.push({
            name: 'checkOutCart'
          })
          break
        case 'Bezorgen':
          this.$router.push({
            name: 'checkOutDeliver'
          })
          break
        case 'Factureren':
          this.$router.push({
            name: 'checkOutInvoice'
          })
          break
        case 'Controleren':
          this.$router.push({
            name: 'checkOutConfirm'
          })
          break
      }
    },
    updateOrder(key, val) {
      this.checkOutStore.updateOrderDetails(key, val)
    },
    sendOrder() {
      if (this.inProgress) return
      this.inProgress = true
      EventBus.emit('orderSubmitting')
      this.$modal.show(
        SendingOrder,
        { text: this.custom.sapPortal ? 'Bestelling voorbereiden' : '' },
        {
          height: 120,
          width: 280,
          adaptive: true,
          name: 'sending',
          clickToClose: false
        }
      )

      const order = this.prepareOrder(this.orderDetails)

      if (
        this.phoneRequired &&
        order.phone &&
        order.phone != this.rootStore.user.phone
      ) {
        userSetting({ phone: order.phone })
        this.rootStore.user.phone = order.phone
      }

      if (!order) {
        this.$modal.hide('sending')
        this.inProgress = false
        return
      }

      if (this.checkOutStore.products.length > 0) {
        //add files to order
        order.cart = cloneDeep(this.checkOutStore.products)
        order.cart.forEach((file) => {
          file = this.prepareProductFile(file)
        })
      }

      if (this.printStoreStore.orderDetails.cart.length > 0) {
        const products = this.preparePrintStoreCart(
          this.printStoreStore.orderDetails.cart
        )

        order.cart = [...order.cart, ...products]
      }

      const isDummy = this.rootStore.theme?.customFields?.checkOut?.dummyOrder
      //console.log('orderRequest', order)
      //return

      const orderRequest = isDummy
        ? Promise.resolve({ dummy: true })
        : saveOrder(order)

      orderRequest
        .then((response) => {
          this.inProgress = false
          this.$modal.hideAll()

          const fileStore = useFileListStore()
          fileStore.removeAllFiles()
          this.printStoreStore.emptyCart()
          this.checkOutStore.resetOrderDetails()

          if (this.custom.sapPortal) {
            this.$modal.show(
              SapOrderConfirm,
              {
                xml: response.xml,
                url: response.callbackUrl
              },
              {
                width: 360,
                height: 'auto',
                clickToClose: false
              }
            )
          } else if (this.custom.orderRedirect && response.redirectUrl) {
            this.$modal.show(
              OrderRedirectConfirm,
              {
                url: response.redirectUrl
              },
              {
                width: 360,
                height: 'auto',
                clickToClose: false
              }
            )
          } else {
            this.$modal.show(
              OrderConfirm,
              {},
              {
                width: 360,
                height: 'auto',
                clickToClose: false
              },
              {
                'before-close': () => {
                  this.step = 'step1'
                }
              }
            )
          }
        })
        .catch((error) => {
          console.log(error)
          this.inProgress = false
          EventBus.emit('orderFailed')
          this.$modal.hide('sending')

          if ('UPLOAD_ERROR' === error.data.error) {
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text:
                  'Uw bestanden zijn niet correct geupload. Uw winkelwagen is geleegd. Probeer de bestanden opnieuw te uploaden.'
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
            const fileStore = useFileListStore()
            fileStore.removeAllFiles()
          } else if ('FILES_MISSING_FROM_SESSION' === error.data.error) {
            const missingFiles = error.data.files || []
            const meerdere = missingFiles.length > 1
            const fileNamesHtml = missingFiles
              .map((f) => `<strong>${f}</strong>`)
              .join('<br>')
            const text = meerdere
              ? `De bestanden<br>${fileNamesHtml}<br>zijn niet correct geupload, de bestanden zijn uit uw upload verwijderd. Upload ze opnieuw om uw order af te kunnen ronden.`
              : `Het bestand<br>${fileNamesHtml}<br>is niet correct geupload, het bestand is uit uw upload verwijderd. Upload het opnieuw om uw order af te kunnen ronden.`
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
            const fileStore = useFileListStore()
            for (const fileName of missingFiles) {
              const file = fileStore.fileList.find(
                (f) => f.fileName === fileName
              )
              if (file) {
                fileStore.removeFile(file.uuid)
                this.checkOutStore.removeFile(file.uuid)
              }
            }
          } else {
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text: 'Neem contact op met de Rijnja Helpdesk'
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
          }
        })
    }
  },
  computed: {
    ...mapStores(useCheckOutStore, usePrintStoreStore),
    deliveryAddressIncludedInFiles() {
      return this.checkOutStore.deliveryAddressIncludedInFiles
    },
    shops() {
      return this.shopsStore.shopsList
    },
    custom() {
      return this.rootStore.theme.customFields
    },
    orderDetails() {
      return this.checkOutStore.orderDetails
    },
    shop() {
      return this.checkOutStore.shop
    },
    activeStep() {
      if (this.$route.name === 'checkOutCart') {
        return this.steps.indexOf('Producten')
      } else if (this.$route.name === 'checkOutDeliver') {
        return this.steps.indexOf('Bezorgen')
      } else if (this.$route.name === 'checkOutInvoice') {
        return this.steps.indexOf('Factureren')
      } else if (this.$route.name === 'checkOutConfirm') {
        return this.steps.indexOf('Controleren')
      }
    },
    steps() {
      let steps = ['Producten', 'Bezorgen', 'Factureren', 'Controleren']
      if (
        this.custom.hideDeliverOptions ||
        this.deliveryAddressIncludedInFiles
      ) {
        steps = steps.filter((step) => step !== 'Bezorgen')
      }
      if (this.custom.hideInvoiceOptions) {
        steps = steps.filter((step) => step !== 'Factureren')
      }
      return steps
    },
    customerNumber() {
      return this.checkOutStore.orderDetails.invoiceAddress.newCustomer
        ? 0
        : this.checkOutStore.orderDetails.invoiceAddress.customerNumber || 0
    },
    phoneRequired() {
      return this.custom.checkOut?.phoneRequired
    }
  },
  created() {
    if (isEmpty(this.orderDetails.deliveryAddress)) {
      const deliveryAddress = this.setDeliveryAddress()

      this.checkOutStore.updateOrderDetails('deliveryAddress', deliveryAddress)
    }

    if (!this.orderDetails.invoiceAddress.customerNumber) {
      const invoiceDetails = this.getDefaultInvoiceDetails()

      this.checkOutStore.updateOrderDetails(
        'invoiceAddress',
        invoiceDetails.address
      )

      this.checkOutStore.updateProject(invoiceDetails.project)

      this.checkOutStore.updateOrderDetails(
        'reference',
        invoiceDetails.reference
      )
    }

    this.checkOutStore.getTheShop({
      deliveryAddress: this.orderDetails.deliveryAddress,
      invoiceId: this.customerNumber,
      secure: this.secure
    })

    this.checkOutStore.updateOrderDetails('secure', this.secure)

    EventBus.on('submitOrder', this.sendOrder)
  },
  unmounted() {
    EventBus.off('submitOrder', this.sendOrder)
  },
  watch: {
    'orderDetails.deliveryAddress': function () {
      this.checkOutStore.getTheShop({
        deliveryAddress: this.orderDetails.deliveryAddress,
        invoiceId: this.customerNumber,
        secure: this.secure
      })
    },
    'orderDetails.invoiceAddress': function (newVal, oldVal) {
      if (newVal.zipCode !== oldVal.zipCode || newVal.city !== oldVal.city) {
        this.checkOutStore.getTheShop({
          deliveryAddress: this.orderDetails.deliveryAddress,
          invoiceId: this.customerNumber,
          secure: this.secure
        })
      }
    },
    shop: function (val) {
      this.checkOutStore
        .getTheDates({
          shopId: this.shop.id,
          invoiceId: this.customerNumber,
          secure: this.secure
        })
        .then((dates) => {
          const storedDate = this.orderDetails.deliveryDate

          // Validate stored date: reset if empty, spoed, or no longer active
          const needsNewDate =
            isEmpty(storedDate) ||
            (storedDate.title !== 'spoed' &&
              !dates.some(
                (date) => date.active && date.value === storedDate.value
              ))

          if (needsNewDate) {
            let delDate = dates.find((date) => date.active)
            if (delDate) {
              delDate.time = delDate.times[0].time
              this.checkOutStore.updateOrderDetails('deliveryDate', delDate)
            }
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
$product-order-shadow: rgba(96, 96, 96, 0.2) 0 4px 13px;

.order-steps-container {
  padding-top: 50px;
}

@media screen and (min-width: 620px) {
  .order-steps-container {
    top: 95px;
  }
}
@media screen and (min-width: 768px) {
  .order-steps-container {
    top: 95px;
  }
}

@media screen and (min-width: 1024px) {
  .order-steps-container {
    top: max(50vh - 423px, 46px);
    margin-left: 298px;
    max-width: 1210px;
    margin-right: 32px;
  }
}

/* @media screen and (min-width: 620px) {
  .order-steps-container {
    top: 95px;
  }
}
@media screen and (min-width: 768px) {
  .order-steps-container {
    top: 110px;
  }
} */

/* @media screen and (min-width: 1024px) {
  .order-steps-container {
    top: max(50vh - 442px, 30px);
    margin-left: 298px;
    max-width: inherit;
    margin-right: 32px;
  }
} */
/* @media screen and (min-width: 1200px) {
  .order-steps-container {
    top: 28px;
    margin-left: 298px;
  }
}
 */
</style>
